import { useEffect, useState } from "react";
import Auth from "../Auth";
import { useAdminAccess } from "../hooks/useAdminAccess";

const resources = ["documents", "projects", "cardsets", "invocations", "chats", "completions"];
const columns = [
  "id",
  "projectId",
  "documentId",
  "source",
  "createdAt",
  "updatedAt",
  "status",
  "model",
  "provider",
  "wordCount",
];
type DiagnosticRow = Record<string, string | number | null>;

// Render only recognized scalar columns. The server owns the metadata projection;
// this table must not fall back to displaying raw records or nested content.
function MetadataTable({ rows }: { rows: DiagnosticRow[] }) {
  const visibleColumns = columns.filter((column) =>
    rows.some((row) => row[column] !== undefined && row[column] !== null),
  );
  if (!rows.length) return <p>No records found.</p>;

  return (
    <div className="overflow-x-auto">
      <table className="text-sm w-full text-left">
        <thead>
          <tr>
            {visibleColumns.map((column) => (
              <th
                key={column}
                className="p-2"
              >
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.id}
              className="border-t"
            >
              {visibleColumns.map((column) => (
                <td
                  key={column}
                  className="p-2 break-all"
                >
                  {typeof row[column] === "object" ? "" : String(row[column] ?? "")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function UserDiagnostics({ userId }: { userId: string }) {
  const { can } = useAdminAccess();
  const [data, setData] = useState<Record<string, DiagnosticRow[]> | null>(null);
  const [error, setError] = useState("");
  const allowed = resources.some((resource) => can(`${resource}.readMetadata`));
  useEffect(() => {
    let active = true;
    setData(null);
    setError("");
    if (allowed) {
      Auth.fetchWithIdToken(`/api/admin/user/${encodeURIComponent(userId)}/diagnostics`)
        .then((result) => {
          if (active) setData(result.resources);
        })
        .catch(() => {
          if (active) setError("Unable to load account diagnostics.");
        });
    }
    return () => {
      active = false;
    };
  }, [userId, allowed]);
  if (!allowed) return null;
  return (
    <section className="bg-white border rounded-lg p-5 mt-6">
      <h2 className="text-xl font-semibold">Account diagnostics</h2>
      <p className="text-sm text-gray-600">
        Metadata only. Up to 50 recent records per type; writing, prompts, and generated content are
        excluded.
      </p>
      {error && <p role="alert">{error}</p>}
      {!data && !error && <p>Loading diagnostics…</p>}
      {data &&
        resources
          .filter((resource) => can(`${resource}.readMetadata`))
          .map((resource) => {
            const rows = data[resource] || [];
            return (
              <details
                key={resource}
                className="border-t py-3"
              >
                <summary className="capitalize cursor-pointer">
                  {resource} ({rows.length} shown)
                </summary>
                <MetadataTable rows={rows} />
              </details>
            );
          })}
    </section>
  );
}
